import { Box, Button, Card, CardContent, Checkbox, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, IconButton, Link, NoSsr, TextField, Tooltip, Typography, Collapse, Switch } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { Add, Edit, Cancel, Close, Send, LinkedIn, PictureAsPdfSharp, CheckCircle, HelpOutlineRounded, KeyboardArrowDown, PregnantWomanSharp, OpenInNew } from '@material-ui/icons'
import i18n from 'i18next'
import { useRouter } from 'next/router'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { mapByIds, useAuth, useFetch } from '../../../components/context/useFetch'
import Layout from '../../../components/layout'
import { useAlert, useBatch, useChangeHandler, useRemove, useUpdate } from '../../../components/reducer'
import Head from 'next/head'
import ReactMarkdown from 'react-markdown'
import { Post } from '../../../components/dashboard/Post2'
import Documents from '../../../components/profile_components/documents'
import { PortfolioContent } from '../../t/[pid]'
import CloseIcon from '@material-ui/icons/Close'
import VisibilityIcon from '@material-ui/icons/Visibility';
import { ProfileCoverLetter as EditCoverLetter } from '../../../components/profile_components/cover_letter'
import allPagesProps from '../../../components/common/all_pages_props'

export async function getServerSideProps(context) {
  return {
    props: await allPagesProps(context)
  }
}

export default function JobApplication() {
  return (
    <Layout pageTitle="pages_titles.job_application">
      <Container>
        <NoSsr>
          <ComponentContent />
        </NoSsr>
      </Container>
    </Layout>
  )
}

// Returns an object of document types that are not presents on user's documents or not checked (selected)
const useMissingRequiredDocuments = () => {
  const documents = useSelector((state) => state.documents)
  const documentTypes = useSelector((state) => state.documentTypes)

  const requiredDocumentTypeIds = [Object.values(documentTypes).find(({ slug }) => slug === 'cv').id, ...useSelector((state) => state.offer.data.required_document_type_ids)]
  const selectDocumentIds = useSelector((state) => state.application.selected_document_ids || [])

  const selectedDocumentTypeIds = selectDocumentIds
    .filter((id) => documents.data[id])
    .map((documentId) => parseInt(documents.data[documentId].document_type.id))

  const difference = (arr1, arr2) => arr1.filter((element) => !arr2.includes(element))

  return difference(requiredDocumentTypeIds, selectedDocumentTypeIds).map((id) => documentTypes[id])
}

const isAnswerPresent = (value) => {
  return Boolean(value) && (typeof value === 'string' ? true : Object.keys(value).length > 0)
}

const hasAnySurveyAnswer = (survey, answers) => {
  const questionIds = survey.questions.map(({ id }) => id)

  return Boolean(questionIds.find((id) => isAnswerPresent(answers[id])))
}

const interpolateTags = (originalText, interpolations) => {
  const interpolateText = originalText

  const interpolatedText = Object.entries(interpolations).reduce((interpolatedString, [slug, value]) => interpolatedString.replaceAll(`{${slug}}`, value), interpolateText)

  const wrongTypedTags = interpolatedText.match(/\{([^{}]*)\}/g)

  return (wrongTypedTags || []).reduce((interpolatedString, slug) => interpolatedString.replaceAll(`${slug}`, ''), interpolatedText)
}

const groupByDocumentType = (documents) =>
  documents.reduce(
    (allDocuments, currentDocument) => {
      allDocuments[currentDocument.document_type.id] = allDocuments[currentDocument.document_type.id] || []
      allDocuments[currentDocument.document_type.id].push(currentDocument)
      return allDocuments
    },
    {}
  )

const ComponentContent = () => {
  const router = useRouter()
  const { pid } = router.query

  if (!pid) {
    return null
  }

  const profile = useSelector((state) => state.profile)

  return useFetch(
    ({ fetch }) => ({
      offer: fetch(`job_offers/${pid}`, { queryParams: { include: 'company,surveys.questions.choices,contract_type' } }),
      application: fetch(`job_offers/${pid}/job_application`).then((application) => application || {}),
      documents: fetch('profile/documents').then(({ data, included }) => ({
        data: mapByIds(data),
        included
      })),
      technologies: fetch('technologies').then((results) => mapByIds(results.data)),
      documentTypes: fetch('document_types').then(mapByIds),
      jobs: fetch('jobs'),
      editMode: Promise.resolve(false),
      generateResume: Promise.resolve(false)
    }),
    { cache: false },
    ({
      offer: {
        data: { label }
      },
      application,
      documents,
      documentTypes
    }) => {
      const auth = useAuth()
      const update = useUpdate()
      const batch = useBatch()
      const alert = useAlert()
      const handler = useChangeHandler(application, 'application')
      const getDocumentTypeObject = (index) => documentTypes[index]
      const [preview, setPreview] = useState(false)

      const offer = useSelector((state) => state.offer)
      const editMode = useSelector((state) => state.editMode)
      const cvDocumentType = Object.values(documentTypes).find(({ slug }) => slug === 'cv')

      const tagValues = {
        company_name: offer.included.companies[offer.data.company.id].name,
        job_offer: offer.data.label
      }

      const [disable, setDisable] = useState(false)
      const [showRecapSimilar, setShowRecapSimilar] = useState(false)

      useEffect(() => {
        if (!application.id) {
          batch(['application'], {
            cover_leter: (profile.default_cover_letter || '').length > 0 ? interpolateTags(profile.default_cover_letter, tagValues) : '',
            selected_document_ids: Object.values(documents.data).map(({ id }) => parseInt(id)),
            send_cover_leter: (profile.default_cover_letter || '').length > 0
          })
        }
      }, [])

      const submitHandler = (event) => {
        event.preventDefault()

        setDisable(true)

        const applicationToBeSent = { ...application }
        if (!application.send_cover_leter) {
          applicationToBeSent.cover_leter = ''
        }

        const requests = [auth.fetch(`job_offers/${pid}/job_application`, { method: 'POST', body: { application: applicationToBeSent } })]

        Promise.all(requests)
          .then(() => {
            setDisable(true)
            setShowRecapSimilar(true)

            if (typeof fbq !== 'undefined') {
              fbq('track', 'Purchase', { value: 0.0, currency: 'USD' })
            }
          })
          .catch(() => {
            alert('error', <Trans i18nKey="alerts.saved_error" />)
          })
      }

      const selectedDocumentHandler = (id) => (event) => {
        const ids = application.selected_document_ids || []

        event.target.checked
          ? update(['application', 'selected_document_ids'], [...ids, parseInt(id)])
          : update(
            ['application', 'selected_document_ids'],
            ids.filter((e) => e !== parseInt(id))
          )
      }

      const hasMissingInformations = !profile.phone || !profile.user_attributes.email
      const missingDocuments = useMissingRequiredDocuments()

      const hasCV = () =>
        (application.selected_document_ids || [])
          .filter((id) => documents.data[id])
          .map((id) => getDocumentTypeObject(documents.data[id].document_type.id))
          .some(({ slug }) => slug === 'cv')

      const isValid = () => profile.phone && profile.user_attributes.email && (profile.user_attributes?.email || profile.user.email) && hasCV()

      const showAlertError = hasMissingInformations || missingDocuments.length > 0

      const missingDocumentTypes = useMissingRequiredDocuments()

      const openDocument = (documentId) => () => {
        auth.fetch(`profile/documents/${documentId}/download`).then(({ url }) => {
          window.open(url)
        })
      }

      return (
        <Fragment>
          <Head>
            <title>
              {i18n.t('job_offer.job_apply.apply_for')} {Object.values(offer.included.companies)[0].name}
              {' — '}
              {offer.data.label}
            </title>
          </Head>

          <Box mt={10} mb={3} maxWidth={900}>
            <Typography variant="h2" component="h1">
              <Trans i18nKey="job_offer.job_apply.apply_for" /> {offer.data.label}
            </Typography>

            <Typography variant="h6" color="textSecondary">
              <Trans i18nKey="job_offer.job_apply.offer" /> {Object.values(offer.included.companies)[0].name}
              {' - '}
              {offer.included.contract_types[offer.data.contract_type_id].label}
            </Typography>
          </Box>

          {editMode === 'info' && <EditProfileDialog />}

          {Boolean(application.id) && (
            <Fragment>
              <Grid container justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <AlreadyApplied />

                  <Box my={2}>
                    <Typography align="center">
                      <Button color="primary" variant="outlined" onClick={() => setPreview(true)}>
                        <VisibilityIcon />&nbsp;Consulter la candidature envoyée
                      </Button>
                    </Typography>
                  </Box>

                  <SimilarJobOffersContent id={offer.data.id} limit={3} />
                </Grid>
              </Grid>
            </Fragment>
          )}

          {preview && <PreviewProfile onClose={() => setPreview(false)} />}

          {!Boolean(application.id) &&
            <form onSubmit={submitHandler}>
              <Grid container spacing={2} alignItems="stretch" direction="row">
                <Grid item xs={12} md={6}>
                  <ExistingInfo applied={Boolean(application.id)} />
                </Grid>

                <Grid item xs={12} md={6}>
                  {!application.id && (
                    <Fragment>
                      {/* Section : Curriculum vitæ (CV) */}
                      <Box mb={2}>
                        <Card variant="outlined">
                          <CardContent>
                            <Grid container justifyContent="space-between" alignItems="baseline">
                              <Typography variant="h2" className="sectionTitle">
                                <Trans i18nKey="job_offer.job_apply.cv" />
                              </Typography>

                              <Button size="small" color="primary" endIcon={<Add />} onClick={() => update(['editMode'], 'cv')}>
                                <Trans i18nKey="job_offer.job_apply.cv_add" />
                              </Button>

                              {['cv', 'other'].includes(editMode) &&
                                <EditDocumentsDialog />
                              }
                            </Grid>

                            <Box>
                              {/* Return seulement les documents de type CV */}
                              {documents && Object.values(documents.data).filter(({ document_type: { id } }) => id == cvDocumentType.id).map((docFile) => (
                                <Grid container justifyContent="space-between">
                                  <FormGroup>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={(application.selected_document_ids || []).includes(parseInt(docFile.id))}
                                          onChange={selectedDocumentHandler(docFile.id)}
                                          name={docFile.id}
                                          color="primary"
                                        />
                                      }
                                      label={docFile.file_blob.filename}
                                    />
                                  </FormGroup>
                                  <IconButton onClick={openDocument(docFile.hashid)}>
                                    <OpenInNew />
                                  </IconButton>
                                </Grid>
                              ))}
                            </Box>

                            {/* Return seulement si CV Made in TrustMe n'a pas été créé */}
                            {missingDocumentTypes.find(({ slug }) => slug === 'cv') && (
                              <Box mt={2}>
                                <Link href="/profile/generator" target="_blank">
                                  <Button size="small" color="primary" variant="contained" startIcon={<PictureAsPdfSharp />} onClick={() => update(['generateResume'], true)}>
                                    <Trans i18nKey="job_offer.job_apply.create_resume" />
                                  </Button>
                                </Link>
                              </Box>
                            )}

                            <Collapse in={showAlertError}>
                              <Box mt={3}>
                                <AlertError />
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      </Box>

                      {/* Section : Lettre de motivation */}
                      <Box mb={2}>
                        <Card variant="outlined">
                          <CardContent>
                            <Grid container justifyContent="space-between" alignItems="baseline">
                              <Typography variant="h2" className="sectionTitle">
                                <Trans i18nKey="job_offer.job_apply.motivation" />
                                <Tooltip title={i18n.t('job_offer.job_apply.cover_letter_tooltip_text')}>
                                  <HelpOutlineRounded style={{ verticalAlign: 'middle' }} fontSize="small" />
                                </Tooltip>
                                <br />
                                <small>
                                  (<Trans i18nKey="job_offer.job_apply.optional" />)
                                </small>
                              </Typography>

                              {editMode === 'cover' && <EditCoverDialog />}

                              <Button size="small" color="primary" endIcon={<Add />} onClick={() => update(['editMode'], 'cover')}>
                                <Trans i18nKey="job_offer.job_apply.motivation_edit" />
                              </Button>
                            </Grid>

                            <FormControlLabel
                              control={<Switch color="primary" checked="checked" name="name" {...handler.checkBox('send_cover_leter')} />}
                              label={i18n.t('job_offer.job_apply.motivation_include')}
                            />

                            {application.send_cover_leter &&
                              <Box mt={2}>
                                <TextField
                                  multiline
                                  variant="outlined"
                                  placeholder={"Message d'introduction à l'entreprise qui recrute"}
                                  {...handler.default('cover_leter')}
                                />
                              </Box>
                            }
                          </CardContent>
                        </Card>
                      </Box>

                      {/* Section : Documents à inclure */}
                      <Box>
                        {/* Return documents sauf de type CV et lettre de motivation */}
                        {documents && Object.values(documents.data).filter(({ document_type: { id } }) => parseInt(id) !== parseInt(cvDocumentType.id)).length > 0 && (
                          <Card variant="outlined">
                            <CardContent>
                              <Grid container justifyContent="space-between" alignItems="baseline">
                                <Typography variant="h2" className="sectionTitle">
                                  <Trans i18nKey="job_offer.job_apply.documents" />
                                </Typography>

                                <Button size="small" color="primary" endIcon={<Add />} onClick={() => update(['editMode'], 'other')}>
                                  <Trans i18nKey="job_offer.job_apply.documents_add" />
                                </Button>
                              </Grid>

                              {/* Trier par type de documents */}
                              {documents && Object
                                .entries(groupByDocumentType(Object.values(documents.data).filter(({ document_type: { id } }) => parseInt(id) !== parseInt(cvDocumentType.id))))
                                .map(([typeId, documents]) => (
                                  <Box mb={2}>
                                    <FormControl component="fieldset">
                                      <FormLabel component="legend">
                                        <Typography variant="caption" component="h3" className="preview">
                                          {getDocumentTypeObject(typeId).label}
                                        </Typography>
                                      </FormLabel>
                                    </FormControl>

                                    {documents.map((docFile) =>
                                      <Grid container justifyContent="space-between">
                                        <FormGroup>
                                          <FormControlLabel
                                            control={<Checkbox checked={(application.selected_document_ids || []).includes(parseInt(docFile.id))} onChange={selectedDocumentHandler(docFile.id)} name={docFile.id} color="primary" />}
                                            label={docFile.file_blob.filename}
                                          />
                                        </FormGroup>
                                        <IconButton onClick={openDocument(docFile.hashid)}>
                                          <OpenInNew />
                                        </IconButton>
                                      </Grid>
                                    )}
                                  </Box>
                                ))}

                              <Collapse in={showAlertError}>
                                <AlertError />
                              </Collapse>
                            </CardContent>
                          </Card>
                        )}
                      </Box>
                    </Fragment>
                  )}
                </Grid>

                {/* Section : Assessment */}
                <Assessment />

                <Grid item xs={12} container justifyContent="space-between">
                  <Button size="large" variant="outlined" onClick={() => router.back()}>
                    <Trans i18nKey="registration.candidat.back" />
                  </Button>

                  <span>
                    {!application.id && (
                      <Button size="large" onClick={() => setPreview(true)} color="secondary">
                        <VisibilityIcon />
                        &nbsp;
                        <Trans i18nKey="profile.generator.preview" />
                      </Button>
                    )}

                    {!application.id && (
                      <Button size="large" variant="contained" color="primary" disabled={disable || !isValid()} onClick={submitHandler}>
                        <Trans i18nKey="job_offer.job_apply.apply" />
                      </Button>
                    )}
                  </span>
                </Grid>

                {showRecapSimilar && <SimilarJobOffers id={pid} closeHandler={() => setShowRecapSimilar(false)} />}
              </Grid>
            </form>
          }
        </Fragment>
      )
    }
  )
}

const EditDocumentsDialog = () => {
  const update = useUpdate()
  const closeHandler = () => update(['editMode'], false)
  const editMode = useSelector(({ editMode }) => editMode)

  return (
    <Dialog open onClose={closeHandler}>
      <DialogContent>
        <Documents slug={editMode} />
      </DialogContent>
    </Dialog>
  )
}

const EditCoverDialog = () => {
  const offer = useSelector(({ offer }) => offer)
  const defaultCoverLetter = useSelector(({ profile }) => profile.default_cover_letter)
  const tagValues = {
    company_name: offer.included.companies[offer.data.company.id].name,
    job_offer: offer.data.label
  }

  const update = useUpdate()
  const closeHandler = () => {
    update(['editMode'], false)
    interpolateTags(defaultCoverLetter, tagValues)
  }

  return (
    <Dialog open onClose={closeHandler}>
      <DialogContent>
        <EditCoverLetter extraSaveHandler={closeHandler} />
      </DialogContent>
    </Dialog>
  )
}

const SimilarJobOffers = (props) => {
  return (
    <Dialog open onClose={(e) => props.closeHandler(false)}>
      <DialogTitle>
        <Typography align="right">
          <IconButton onClick={(e) => props.closeHandler(false)}>
            <Close />
          </IconButton>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="h2" align="center">
          <Trans i18nKey="alerts.candidature_succesful" />
        </Typography>

        <Typography align="center" style={{ fontSize: '200px' }}>
          <img src="/emoji/celebration.png" alt="Celebration" />
        </Typography>

        <SimilarJobOffersContent {...props} />
      </DialogContent>
    </Dialog>
  )
}

const SimilarJobOffersContent = ({ id, limit = 20 }) =>
  useFetch(
    ({ fetch }) => ({
      offers: fetch(`job_offers/${id}/similar_offers`)
    }),
    ({ offers }) => (
      <Fragment>
        {offers.data.length > 0 && (
          // Section : D’autres annonces pourrait vous intéresser
          <Box mt={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h2" className="sectionTitle">
                  <Trans i18nKey="job_offer.similar_job.title" />
                </Typography>

                {Object.values(offers.data).slice(0, limit).map((offer) => (
                  <Post offer={offer} />
                ))}
              </CardContent>
            </Card>
          </Box>
        )}
      </Fragment>
    )
  )

const AlertError = () => {
  const update = useUpdate()

  const missingDocumentTypes = useMissingRequiredDocuments()

  const documentLabels = missingDocumentTypes.map(({ label }) => label).join(', ')

  return (
    <Alert severity="error">
      {i18n.t('job_offer.job_apply.alert', { documents: documentLabels })}
    </Alert>
  )
}

const MissingAnswerAlertError = () => {
  return (
    <Box mt={1}>
      <Alert severity="error">{i18n.t('alerts.survey')}</Alert>
    </Box>
  )
}

export const CoverLetter = () => {
  const coverLetter = useSelector((state) => state.application.data?.cover_leter || state.application.cover_leter)

  if (!coverLetter) {
    return null
  }

  return (
    <Container maxWidth="md">
      <Box mb={3} mt={5}>
        <Card>
          <CardContent>
            <Grid container>
              <Grid item xs={1} container alignItems="center">
                <Send style={{ transform: 'rotate(-45deg)', width: '100px' }} />
              </Grid>

              <Grid item xs={11}>
                <ReactMarkdown skipHtml linkTarget="_blank">
                  {coverLetter}
                </ReactMarkdown>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Container>
  )
}

const AlreadyApplied = () => (
  <Alert variant="filled" severity="success">
    <Trans i18nKey="job_offer.job_apply.alert_already_applied" />
  </Alert>
)

const ExistingInfo = ({ applied, hasRequiredDocuments }) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const offer = useSelector(({ offer }) => offer)
  const profile = useSelector(({ profile }) => profile)
  const user = useAuth().getToken()
  const userEmail = user ? user.email : ' '

  const update = useUpdate()

  const handleClickOpen = () => {
    update(['editMode'], 'info')
  }

  const handleClose = () => {
    update(['editMode'], false)
  }
  const openpop = Boolean(anchorEl)

  return (
    <Fragment>
      {/* Section : Informations essentielles */}
      {!applied && (
        <Card variant="outlined" style={{ height: '100%' }}>
          <CardContent>
            <Grid container justifyContent="space-between" alignItems="baseline">
              <Typography variant="h2" className="sectionTitle">
                <Trans i18nKey="job_offer.job_apply.title" />
              </Typography>
              <Button size="small" color="primary" endIcon={<Edit />} onClick={handleClickOpen}>
                <Trans i18nKey="job_offer.job_apply.edit" />
              </Button>
            </Grid>

            <Box mb={2}>
              <Typography component="h3" variant="caption" className="preview">
                <Trans i18nKey="profile.candidat.profile_tab.title" />
              </Typography>
              <Typography component="p" variant="h6">
                {profile.title ||
                  (!applied && (
                    <Trans i18nKey="job_offer.job_apply.form.empty" />
                  ))}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography component="h3" variant="caption" className="preview">
                <Trans i18nKey="profile.candidat.profile_tab.first_name" />
              </Typography>
              <Typography component="p" variant="h6">
                {profile.first_name}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography component="h3" variant="caption" className="preview">
                <Trans i18nKey="profile.candidat.profile_tab.last_name" />
              </Typography>
              <Typography component="p" variant="h6">
                {profile.last_name}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography component="h3" variant="caption" className="preview">
                {i18n.t('profile.candidat.profile_tab.mail')}
              </Typography>
              <Typography component="p" variant="h6">
                {userEmail}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography component="h3" variant="caption" className="preview">
                <Trans i18nKey="profile.candidat.profile_tab.phone" />
              </Typography>
              <Typography component="p" variant="h6">
                {profile.phone}
              </Typography>
            </Box>

            {!profile.description && !applied && (
              <Box mb={2}>
                <Typography component="h3" variant="caption" className="preview">
                  <Trans i18nKey="profile.candidat.profile_tab.description.label" />
                </Typography>
                <Typography component="p" variant="h6">
                  <Trans i18nKey="job_offer.job_apply.form.empty" />
                </Typography>
                <Typography component="p" variant="body2">
                  <Trans i18nKey="profile.candidat.profile_tab.description.placeholder" />
                </Typography>
              </Box>
            )}

            <Box>
              <Typography component="h3" variant="caption" className="preview">
                <Trans i18nKey="job_offer.job_apply.form.linkedin" />
                <LinkedIn aria-owns={openpop ? 'mouse-over-popover' : undefined} aria-haspopup="true" fontSize="small" style={{ verticalAlign: 'top' }} className="no-scale" />
              </Typography>
              <Typography component="p" variant="h6">
                {profile.linkedin ? <CheckCircle color="secondary" /> : <Cancel color="error" />}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Fragment>
  )
}

export const byLabel = ({ label: labelA }, { label: labelB }) => labelA.localeCompare(labelB)

const EditProfileDialog = () => {
  const { t } = useTranslation()

  const profile = useSelector((state) => state.profile)

  const jobs = useSelector(({ jobs }) => jobs)
  const jobsById = mapByIds(jobs)

  const auth = useAuth()
  const user = auth.getToken()

  const editMode = useSelector((state) => state.editMode)
  const update = useUpdate()

  useEffect(() => {
    update(['user'], { profile, user })
  }, [])

  const editingUser = useSelector((state) => state.user)

  const formRef = useRef()

  if (!editingUser) {
    return null
  }

  const handleClose = () => {
    update(['editMode'], false)
  }

  const handler = useChangeHandler(editingUser, 'user')

  const submitHandler = (event) => {
    event.preventDefault()

    const { first_name, last_name, job_id, title, phone, linkedin, description } = editingUser.profile

    auth
      .fetch('profile', {
        method: 'PATCH',
        body: {
          profile: {
            first_name, last_name, job_id, title, phone, linkedin, description,
            user_attributes: {
              email: editingUser.profile.user_attributes.email,
              id: editingUser.profile.user_attributes.id
            }
          }
        }
      })
      .then(() => {
        update(['profile'], editingUser.profile)
        handleClose()
      })
  }

  return (
    <Dialog onClose={handleClose} open={editMode === 'info'} fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography component="h2" variant="h6">
            <Trans i18nKey="job_offer.job_apply.edit" />
          </Typography>

          <IconButton onClick={handleClose}>
            <Close fontSize="small" />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={submitHandler}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                popupIcon={<KeyboardArrowDown />}
                options={jobs.filter(({ parent_job_id }) => Boolean(parent_job_id))}
                getOptionLabel={(option) => option.label}
                filterSelectedOptions
                noOptionsText={i18n.t('noOptionsText')}
                renderInput={(params) => <TextField {...params} required variant="filled" label={i18n.t('profile.candidat.profile_tab.jobs')} InputProps={{ ...params.InputProps, disableUnderline: true }} />}
                groupBy={(option) => option.parent_job_id && jobsById[option.parent_job_id]?.label}
                {...handler.complete('id', jobs)('profile', 'job_id')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="filled"
                placeholder={t('profile.candidat.profile_tab.title_placeholder')}
                label={<Trans i18nKey="profile.candidat.profile_tab.title" />}
                fullWidth
                {...handler.default('profile', 'title')}
                required
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                variant="filled"
                label={<Trans i18nKey="profile.candidat.profile_tab.first_name" />}
                fullWidth {...handler.default('profile', 'first_name')}
                required
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                variant="filled"
                label={<Trans i18nKey="profile.candidat.profile_tab.last_name" />}
                fullWidth
                {...handler.default('profile', 'last_name')}
                required
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                variant="filled"
                label={<Trans i18nKey="profile.candidat.profile_tab.mail" />}
                fullWidth
                {...handler.default('user', 'email')}
                disabled
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                variant="filled"
                label={<Trans i18nKey="profile.candidat.profile_tab.phone" />}
                fullWidth {...handler.default('profile', 'phone')}
                required
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="filled"
                label={<Trans i18nKey="job_offer.job_apply.form.linkedin" />}
                fullWidth {...handler.default('profile', 'linkedin')}
                InputProps={{ disableUnderline: true }}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                variant="filled"
                multiline
                label={<Trans i18nKey="profile.candidat.profile_tab.description.label" />}
                {...handler.default('profile', 'description')}
                placeholder={t('profile.candidat.profile_tab.description.placeholder')}
                InputProps={{ disableUnderline: true }}
              />
            </Grid>
          </Grid>

          <Button type="submit" ref={formRef} style={{ visibility: 'hidden' }} />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => formRef.current.click()} color="primary">
          <Trans i18nKey="set_password.save" />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const Assessment = () => {
  const surveys = useSelector((state) => state.offer.included.surveys)

  if (!surveys) return null

  return (
    <Fragment>
      {Object.values(surveys).map((survey) => (
        <Surveys survey={survey} />
      ))}
    </Fragment>
  )
}

const Surveys = ({ survey }) => {

  const useMissingAnswers = () => {
    const answers = useSelector((state) => state.application.assessment_answers || {})
    const surveys = useSelector((state) => Object.values(state.offer.included.surveys || []))

    return surveys.filter((survey) => survey.required && !hasAnySurveyAnswer(survey, answers))
  }

  const missingAnswers = useMissingAnswers()

  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h2" className="sectionTitle">
            {survey.introduction}
          </Typography>

          <Questions survey={survey} />

          <Collapse in={missingAnswers.length > 0}>
            <MissingAnswerAlertError />
          </Collapse>
        </CardContent>
      </Card>
    </Grid>
  )
}

const Questions = ({ survey }) => {
  const questions = useSelector((state) => state.offer.included.questions)

  if (!questions) return null

  return (
    <Fragment>
      {Object.values(survey.questions).map((surveyQuestion) => (
        <Box mb={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="caption" component="h3" className="preview">
                {questions[surveyQuestion.id].title}
              </Typography>
            </FormLabel>
            <FormGroup>
              <QuestionChoices survey={survey} question={questions[surveyQuestion.id]} />
            </FormGroup>
          </FormControl>
        </Box>
      ))}
    </Fragment>
  )
}

const questionsTypes = {
  choices: 'choices',
  open: 'open'
}

const QuestionChoices = ({ survey, question }) => {
  const questionChoices = useSelector((state) => state.offer.included.question_choices)
  const assessmentAnswers = useSelector((state) => state.application.assessment_answers)

  const update = useUpdate()
  const remove = useRemove()

  if (!questionChoices) return null

  useEffect(() => {
    if (!assessmentAnswers) update(['application', 'assessment_answers'], {})
    else update(['application', 'assessment_answers'], assessmentAnswers)
  }, [])

  const saveChoices = (e, choice) => {
    if (e.target.checked) {
      update(['application', 'assessment_answers', question.id], {
        survey_id: survey.id,
        question_label: question.title,
        value: {
          ...(assessmentAnswers[question.id]?.value || {}),
          [choice.id]: choice.label
        }
      })
    } else {
      remove(['application', 'assessment_answers', question.id, 'value', choice.id])
    }
  }

  const openQuestionHandler = ({ target: { value } }) => {
    update(['application', 'assessment_answers', question.id], {
      survey_id: survey.id,
      question_label: question.title,
      value
    })
  }

  if (!assessmentAnswers) {
    return null
  }

  return (
    <Grid container>
      {question.question_type === questionsTypes.open ? (
        Object.values(question.choices).map((choice) => (
          <Grid item sm={6}>
            <FormControlLabel
              control={<Checkbox color="primary" onChange={(e) => saveChoices(e, questionChoices[choice.id])} checked={Boolean(assessmentAnswers[question.id]?.value?.[choice.id])} />}
              label={questionChoices[choice.id].label}
            />
          </Grid>
        ))
      ) : (
        <TextField
          fullWidth
          id="outlined-multiline-static"
          multiline
          rows={8}
          variant="filled"
          onChange={openQuestionHandler}
          value={(assessmentAnswers[question.id] || {}).value || ''}
        />
      )}
    </Grid>
  )
}

const PreviewProfile = ({ onClose }) => {
  const technologies = useSelector(({ technologies }) => technologies)
  const application = useSelector(({ application }) => application)
  const documentTypes = useSelector(({ documentTypes }) => documentTypes)
  const profile = useSelector((state) => state.profile)
  const documents = useSelector(({ documents }) => documents)

  const candidate = useSelector((state) => state.candidate)

  const update = useUpdate()

  useEffect(() => {
    update(['candidate'], {
      data: profile,
      included: {
        document_types: documentTypes,
        documents: mapByIds(
          Object.values(documents.data).filter(({ id }) => (application.selected_document_ids || []).includes(parseInt(id)))
        ),
        backgrounds: mapByIds(profile.backgrounds),
        projects: mapByIds(profile.projects),
        project_pictures: mapByIds(profile.projects.flatMap(({ pictures }) => pictures)),
        candidates: { [profile.id]: profile },
        candidates_technologies: mapByIds(profile.candidates_technologies),
        users: { [profile.user.id]: profile.user },
        technologies,
        job_offers: {}
      }
    })
  }, [application, profile])

  if (!candidate) {
    return null
  }

  return (
    <Dialog open onClose={onClose} fullScreen>
      <DialogContent>
        <Box mb={3}>
          <Typography align="right">
            <Button onClick={() => onClose()} color="secondary" variant="contained">
              <CloseIcon /> {i18n.t('close')}
            </Button>
          </Typography>
        </Box>
        <Box mb={3}>
          <PortfolioContent />
          <CoverLetter />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
